import { PayloadAction } from "@reduxjs/toolkit";
import {
  createKeyPersonProposalContactInfoFetchError,
  createKeyPersonProposalContactInfoFetchSuccess,
  createKeyPersonProposalContactInfoLoading,
  createKPProposalInfoError,
  createKPProposalInfoLoading,
  createKPProposalInfoSuccess,
  GET_KEY_PERSON_CONTACT_INFO,
  KP_ROW_SCHEMA,
  KPUpdateProposalRowData,
  UPDATE_ROW_KEY_PERSON_PROPOSAL_INFO,
} from "app/redux/features/create-key-person-proposal";
import {
  GET_POLICY_ADMIN_FUNDING,
  UPDATE_POLICY_ADMIN_FINANCING_MODEL,
  POLICY_ADMIN_FINANCING_MODEL_UPDATE_PAYLOAD,
  updatePolicyAdminFinancingModelLoading,
  updatePolicyAdminFinancingModelSuccess,
  updatePolicyAdminFinancingModelError,
} from "app/redux/features/policy-admin-financing-model";
import { makeRequest } from "app/utils/makeRequest";
import { GET_PROPOSAL_PROGRESS_DATA } from "app/redux";
import { all, call, put, takeLatest } from "redux-saga/effects";

function* KPProposalContactInfoRequest(
  actions: PayloadAction<{
    proposalId: string;
  }>,
): Generator<any, void, any> {
  try {
    const {
      payload: { proposalId },
    } = actions;
    yield put(createKeyPersonProposalContactInfoLoading());
    const response = yield call(() => makeRequest("kpiProposalContactInfo").get(proposalId, true));
    yield put(createKeyPersonProposalContactInfoFetchSuccess(response.data));
  } catch (err: any) {
    yield put(createKeyPersonProposalContactInfoFetchError(err));
  }
}

function* KPProposalInfoRequest(
  actions: PayloadAction<{
    proposalId: string;
    userId: string;
  }>,
): Generator<any, void, any> {
  try {
    const {
      payload: { proposalId, userId },
    } = actions;
    yield put(createKPProposalInfoLoading());
    const response = yield call(() =>
      makeRequest("policyAdminFinancingModel").get(`${proposalId}/${userId}`, true, false),
    );
    yield put(
      createKPProposalInfoSuccess({
        ...response.data?.data,
        rows: response.data?.data?.rows
          ?.map((el: any) => ({ ...el, isChanged: false }))
          ?.sort((a: any, b: any) => {
            const aPolicyYear = Number(a.policyYear) || 0;
            const bPolicyYear = Number(b.policyYear) || 0;
            if (aPolicyYear !== bPolicyYear) {
              return aPolicyYear - bPolicyYear;
            }
            // If policyYear is the same, sort by year
            const aYear = Number(a.year) || 0;
            const bYear = Number(b.year) || 0;
            return aYear - bYear;
          }),
      }),
    );
  } catch (err: any) {
    yield put(createKPProposalInfoError(err));
  }
}

function* KPProposalUpdateRow(
  actions: PayloadAction<{
    data: KP_ROW_SCHEMA;
  }>,
): Generator<any, void, any> {
  const {
    payload: { data },
  } = actions;
  yield put(KPUpdateProposalRowData({ data }));
}

function* updatePolicyAdminFinancingModelRequest(
  actions: PayloadAction<{
    proposalId: string;
    pendingId: string;
    data: POLICY_ADMIN_FINANCING_MODEL_UPDATE_PAYLOAD[];
  }>,
): Generator<any, void, any> {
  try {
    const {
      payload: { proposalId, pendingId, data },
    } = actions;
    yield put(updatePolicyAdminFinancingModelLoading());
    const response = yield call(() =>
      makeRequest("updatePolicyAdminFinancingModel").put(
        `${proposalId}/${pendingId}`,
        { data },
        true,
        false,
      ),
    );
    yield put(updatePolicyAdminFinancingModelSuccess(response.data));
    // Refetch latest table data after a successful update
    const refetchResponse = yield call(() =>
      makeRequest("policyAdminFinancingModel").get(`${proposalId}/${pendingId}`, true, false),
    );
    yield put(
      createKPProposalInfoSuccess({
        ...refetchResponse.data?.data,
        rows: refetchResponse.data?.data?.rows
          ?.map((el: any) => ({ ...el, isChanged: false }))
          ?.sort((a: any, b: any) => {
            const aPolicyYear = Number(a.policyYear) || 0;
            const bPolicyYear = Number(b.policyYear) || 0;
            if (aPolicyYear !== bPolicyYear) {
              return aPolicyYear - bPolicyYear;
            }
            // If policyYear is the same, sort by year
            const aYear = Number(a.year) || 0;
            const bYear = Number(b.year) || 0;
            return aYear - bYear;
          }),
      }),
    );
    // Update proposal progress
    yield put({
      type: GET_PROPOSAL_PROGRESS_DATA,
      payload: { address: "kpiProgress", key: proposalId, requestParams: true },
    });
  } catch (err: any) {
    yield put(updatePolicyAdminFinancingModelError(err));
  }
}

function* createKeyPersonProposal() {
  yield takeLatest(GET_KEY_PERSON_CONTACT_INFO, KPProposalContactInfoRequest);
  yield takeLatest(GET_POLICY_ADMIN_FUNDING, KPProposalInfoRequest);
  yield takeLatest(UPDATE_ROW_KEY_PERSON_PROPOSAL_INFO, KPProposalUpdateRow);
  yield takeLatest(UPDATE_POLICY_ADMIN_FINANCING_MODEL, updatePolicyAdminFinancingModelRequest);
}

export default function* createKeyPersonProposalSaga() {
  yield all([createKeyPersonProposal()]);
}
